import { useContext, useState } from 'react';
import {
  Alert, Box, Button, Chip, Divider, IconButton, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography,
} from '@mui/material';
import { AddOutlined, DeleteOutline, EditOutlined, KeyboardArrowDownOutlined } from '@mui/icons-material';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../../shared/services/survey/survey-admin.service';
import { SimpleRuleDialog, RuleSaveApi, SimpleRuleDraft } from '../../../dialogs/surveys/simple-rule-dialog';
import { AdvancedRuleDialog } from '../../../dialogs/surveys/advanced-rule-dialog';
import { BulkRuleCreateDialog } from '../../../dialogs/surveys/bulk-rule-create-dialog';
import { detectSingleQuestionMode, summarizeRule } from '../../../../shared/utils/survey-rules/formula';
import {
  ISurveyQuestionGroupEmissionRule,
  ISurveyQuestionGroupItem,
} from '../../../../shared/services/survey/survey.model';

export interface CalculationsApi extends RuleSaveApi {
  deleteEmissionRule: (groupId: string, ruleId: string) => Promise<unknown>;
}

interface Props {
  groupId: string;
  groupItems: ISurveyQuestionGroupItem[];
  /** All rules; the panel splits emission rules from intermediate (adjustment) values. */
  rules: ISurveyQuestionGroupEmissionRule[];
  /** Partner-locked rendering: controls greyed with the reason, never hidden. */
  locked?: boolean;
  api?: CalculationsApi;
  onChanged: () => void;
}

type DialogState =
  | { kind: 'simple'; rule?: ISurveyQuestionGroupEmissionRule }
  | { kind: 'advanced'; rule?: ISurveyQuestionGroupEmissionRule; draft?: SimpleRuleDraft }
  | { kind: 'adjustment'; rule?: ISurveyQuestionGroupEmissionRule }
  | { kind: 'bulk' }
  | null;

/**
 * The one calculations surface: emission rules and intermediate values in plain
 * language, with a single Add menu. Hosted by the survey hub, the question set
 * editor, and (via api injection) the partner Library builder.
 */
export const SurveyCalculationsPanel = ({ groupId, groupItems, rules, locked = false, api, onChanged }: Props) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const deleteRule = api?.deleteEmissionRule
    ?? ((gid: string, rid: string) => surveyAdminService.deleteEmissionRule(gid, rid));
  const saveApi: RuleSaveApi | undefined = api
    ? { createEmissionRule: api.createEmissionRule, updateEmissionRule: api.updateEmissionRule }
    : undefined;

  const [dialog, setDialog] = useState<DialogState>(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);

  const emissionRules = rules.filter(r => !r.isAdjustment);
  const adjustmentRules = rules.filter(r => r.isAdjustment);
  const noFactorCount = emissionRules.filter(r => !r.conversionId).length;
  const adjustmentAliases = adjustmentRules.map(r => r.outputAlias).filter((a): a is string => Boolean(a));

  const openEdit = (rule: ISurveyQuestionGroupEmissionRule) => {
    if (rule.isAdjustment) {
      setDialog({ kind: 'adjustment', rule });
    } else if (detectSingleQuestionMode(rule.inputs ?? [], rule.formula)) {
      setDialog({ kind: 'simple', rule });
    } else {
      setDialog({ kind: 'advanced', rule });
    }
  };

  const confirmDelete = (rule: ISurveyQuestionGroupEmissionRule) => {
    userNotificationsContext.openDialog({
      active: true,
      title: rule.isAdjustment ? 'Delete intermediate value' : 'Delete rule',
      message: `Delete "${rule.name}"?`,
      buttonCopy: 'Delete',
      cancelButtonCopy: 'Cancel',
    }).then(async confirmed => {
      if (!confirmed) return;
      try {
        await deleteRule(groupId, rule.id);
        userNotificationsContext.openSnackbar({ open: true, message: 'Rule deleted', backgroundColor: 'primary.dark' });
        onChanged();
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to delete rule';
        userNotificationsContext.openSnackbar({ open: true, message: msg, backgroundColor: 'text.primary' });
      }
    });
  };

  const closeDialog = () => setDialog(null);
  const handleChanged = () => { onChanged(); };

  const ruleRow = (rule: ISurveyQuestionGroupEmissionRule) => {
    const conditionCount = rule.scopeMetadata?.applyWhen?.allOf?.length ?? 0;
    return (
      <Box key={rule.id} sx={{ px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
        <Stack direction='row' alignItems='center' spacing={1} flexWrap='wrap'>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant='body2' fontWeight={600}>{rule.name}</Typography>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
              {summarizeRule(rule, groupItems)}
            </Typography>
          </Box>
          <Stack direction='row' spacing={0.5} alignItems='center' flexShrink={0}>
            {rule.isAdjustment && rule.outputAlias && (
              <Chip label={`→ ${rule.outputAlias}`} size='small' color='info' variant='outlined' />
            )}
            {conditionCount > 0 && (
              <Chip label='Conditional' size='small' color='info' variant='outlined' />
            )}
            {!rule.isAdjustment && !rule.conversionId && (
              <Tooltip title='This rule produces an activity amount but no kg CO2e until a factor is attached'>
                <Chip label='No factor' size='small' variant='outlined' />
              </Tooltip>
            )}
            <Tooltip title='Edit rule'>
              <span>
                <IconButton size='small' disabled={locked} onClick={() => openEdit(rule)}>
                  <EditOutlined fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Delete rule'>
              <span>
                <IconButton size='small' disabled={locked} onClick={() => confirmDelete(rule)}>
                  <DeleteOutline fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    );
  };

  return (
    <Box>
      {noFactorCount > 0 && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {noFactorCount === 1
            ? '1 calculation has no emission factor and will not produce emissions.'
            : `${noFactorCount} calculations have no emission factor and will not produce emissions.`}
        </Alert>
      )}
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant='body2' color='text.secondary' sx={{ flex: 1, minWidth: 240 }}>
          Rules turn survey answers into emissions: an answer (or formula over answers), multiplied
          by an emission factor, counted when the survey&apos;s responses are committed.
        </Typography>
        <Button
          variant='contained'
          startIcon={<AddOutlined />}
          endIcon={<KeyboardArrowDownOutlined />}
          disabled={locked || groupItems.length === 0}
          onClick={e => setAddMenuAnchor(e.currentTarget)}
        >
          Add calculation
        </Button>
        <Menu anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)} onClose={() => setAddMenuAnchor(null)}>
          <MenuItem onClick={() => { setAddMenuAnchor(null); setDialog({ kind: 'simple' }); }}>
            <ListItemText primary='Simple rule' secondary='An answer, multiplied by an emission factor' />
          </MenuItem>
          <MenuItem onClick={() => { setAddMenuAnchor(null); setDialog({ kind: 'advanced' }); }}>
            <ListItemText primary='Advanced formula' secondary='Combine several answers with calculation steps' />
          </MenuItem>
          <MenuItem onClick={() => { setAddMenuAnchor(null); setDialog({ kind: 'adjustment' }); }}>
            <ListItemText primary='Intermediate value' secondary='A named value other rules can use, produces no emissions' />
          </MenuItem>
          <MenuItem onClick={() => { setAddMenuAnchor(null); setDialog({ kind: 'bulk' }); }}>
            <ListItemText primary='Bulk create from answer options' secondary='One rule per combination of answer options' />
          </MenuItem>
        </Menu>
      </Stack>

      {emissionRules.length === 0 && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          No calculations yet. Without one, responses collect answers but produce no emissions.
        </Typography>
      )}
      <Stack spacing={0.75}>
        {emissionRules.map(ruleRow)}
      </Stack>

      {(adjustmentRules.length > 0 || rules.length > 0) && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant='subtitle2' sx={{ mb: 0.5 }}>Intermediate values</Typography>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1.5 }}>
            Calculated first and usable inside other calculations by name. They do not produce
            emissions on their own.
          </Typography>
          {adjustmentRules.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>None defined.</Typography>
          ) : (
            <Stack spacing={0.75}>
              {adjustmentRules.map(ruleRow)}
            </Stack>
          )}
        </Box>
      )}

      {dialog?.kind === 'simple' && (
        <SimpleRuleDialog
          open
          onClose={closeDialog}
          successHandler={handleChanged}
          groupId={groupId}
          groupItems={groupItems}
          existingRule={dialog.rule}
          api={saveApi}
          onSwitchToAdvanced={draft => setDialog({ kind: 'advanced', draft })}
        />
      )}
      {dialog?.kind === 'advanced' && (
        <AdvancedRuleDialog
          open
          onClose={closeDialog}
          successHandler={handleChanged}
          groupId={groupId}
          groupItems={groupItems}
          existingRule={dialog.rule}
          initialDraft={dialog.draft}
          adjustmentAliases={adjustmentAliases}
          api={saveApi}
        />
      )}
      {dialog?.kind === 'adjustment' && (
        <AdvancedRuleDialog
          open
          onClose={closeDialog}
          successHandler={handleChanged}
          groupId={groupId}
          groupItems={groupItems}
          existingRule={dialog.rule}
          isAdjustment
          adjustmentAliases={adjustmentAliases}
          api={saveApi}
        />
      )}
      {dialog?.kind === 'bulk' && (
        <BulkRuleCreateDialog
          open
          onClose={closeDialog}
          successHandler={() => { handleChanged(); closeDialog(); }}
          groupId={groupId}
          groupItems={groupItems}
          adjustmentAliases={adjustmentAliases}
          api={api ? { createEmissionRule: api.createEmissionRule } : undefined}
        />
      )}
    </Box>
  );
};
